import React, { useContext, useEffect, useRef, useState } from 'react';
import { 
  Box, 
  IconButton, 
  Stack, 
  Typography, 
  useTheme, 
  alpha 
} from '@mui/material';
import { 
  Phone as PhoneIcon, 
  PhoneDisabled as PhoneOffIcon, 
  Videocam as VideoIcon, 
  VideocamOff as VideoOffIcon, 
  Mic as MicIcon, 
  MicOff as MicOffIcon, 
  ScreenShare as ScreenShareIcon, 
  Chat as ChatIcon 
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { VideoContext } from 'components/context/VideoContext';

const VideoCallInterface = () => {
  const location = useLocation();
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  // Destructure all the passed fields
  const {
    // callDuration,
    // display,
    // callStatus,
    // senderId,
    // receiverId,
    // startLocalStream,
    // startLocalAudioStream,
    // acceptCall,
    // endCall,
    // toggleScreenShare,
    ringAudioRef,
    // isScreenSharing,
    // renderMessage,
    historyRef,
    messages,
    setMessage,
    handleFileChange,
    fileName,
    fileempty,
    messageshistory,
    handleKeyDown,
    handleMessageSend,
    handleFileUpload,
    handleFileRemove,
  } = location.state || {};
  const {
    renderMessage,
    localStream,
    remoteStream,
    callDuration,
    display,
    callStatus,
    senderId,
    receiverId,
    startLocalStream,
    startLocalAudioStream,
    endCall,
    acceptCall,
    isScreenSharing,
    toggleScreenShare
    // Access other functions and states
  } = useContext(VideoContext);  const theme = useTheme();
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);

  const handleToggleAudio = () => {
    const stream = localStream;
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMicMuted(!isMicMuted);
      }
    }
  };
  
  const handleToggleVideo = () => {
    const stream = localStream;
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!isVideoOff);
        console.log('isVideoOff set to:', !isVideoOff);
      }
    }
  };
  const [isMounted, setIsMounted] = useState(true);

useEffect(() => {
  return () => {
    setIsMounted(false);
  };
}, []);

if (isMounted && localVideoRef.current && localStream) {
  localVideoRef.current.srcObject = localStream;
}
  return (
    <Box 
      sx={{
        position: 'fixed',
        inset: 0,
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Video Containers */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          position: 'relative', 
          display: 'flex' 
        }}
      >
        {/* Remote Video */}
        <Box sx={{ flexGrow: 1 }}>
          <video 
            ref={remoteVideoRef} 
            autoPlay 
            style={{
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              opacity: isVideoOff ? 0.5 : 1
            }}
          />
        </Box>

        {/* Local Video */}
        {!isVideoOff && (
          <Box
            sx={{
              position: 'absolute',
              bottom: theme.spacing(2),
              right: theme.spacing(2),
              width: '25%',
              zIndex: 20
            }}
          >
            <video 
              ref={localVideoRef} 
              autoPlay 
              muted 
              style={{
                borderRadius: theme.shape.borderRadius,
                boxShadow: theme.shadows[4],
                objectFit: 'cover',
                width: '100%',
                height: '100%',
                opacity: isVideoOff ? 0 : 1,
              }}
            />
          </Box>
        )}
      </Box>

      {/* Call Controls */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: alpha(theme.palette.background.paper, 0.7),
          padding: theme.spacing(2),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: theme.spacing(2)
        }}
      >
        {/* Call Duration */}
        <Typography 
          variant="h6" 
          color="text.primary" 
          sx={{ mr: theme.spacing(4) }}
        >
          {callDuration}
        </Typography>

        <Stack direction="row" spacing={2}>
          {/* Audio Toggle */}
          <IconButton 
            onClick={handleToggleAudio}
            sx={{ 
              backgroundColor: isMicMuted 
                ? theme.palette.error.main 
                : theme.palette.grey[700],
              '&:hover': {
                backgroundColor: isMicMuted 
                  ? theme.palette.error.dark 
                  : theme.palette.grey[600]
              }
            }}
          >
            {isMicMuted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>

          {/* Video Toggle */}
          <IconButton 
            onClick={handleToggleVideo}
            sx={{ 
              backgroundColor: isVideoOff 
                ? theme.palette.error.main 
                : theme.palette.grey[700],
              '&:hover': {
                backgroundColor: isVideoOff 
                  ? theme.palette.error.dark 
                  : theme.palette.grey[600]
              }
            }}
          >
            {isVideoOff ? <VideoOffIcon /> : <VideoIcon />}
          </IconButton>

          {/* Screen Share */}
          <IconButton 
            onClick={toggleScreenShare}
            sx={{ 
              backgroundColor: theme.palette.grey[700],
              '&:hover': { backgroundColor: theme.palette.grey[600] }
            }}
          >
            <ScreenShareIcon />
          </IconButton>

          {/* Chat */}
          <IconButton 
            onClick={() => renderMessage()} // Assuming renderMessage toggles chat
            sx={{ 
              backgroundColor: theme.palette.grey[700],
              '&:hover': { backgroundColor: theme.palette.grey[600] }
            }}
          >
            <ChatIcon />
          </IconButton>

          {/* End Call */}
          <IconButton 
            onClick={endCall}
            sx={{ 
              backgroundColor: theme.palette.error.main,
              '&:hover': { backgroundColor: theme.palette.error.dark }
            }}
          >
            <PhoneOffIcon />
          </IconButton>

          {/* Accept Call */}
          {callStatus !== 'active' && (
            <IconButton 
              onClick={acceptCall}
              sx={{ 
                backgroundColor: theme.palette.success.main,
                '&:hover': { backgroundColor: theme.palette.success.dark }
              }}
            >
              <PhoneIcon />
            </IconButton>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default VideoCallInterface;
// import React from 'react';
// import { Box, IconButton, Typography, useTheme } from '@mui/material';
// import { keyframes } from '@mui/system';
// import VideoCallPopup from './GroupVideoCall';

// const VideoCallDisplay = ({ 
//   localVideoRef, 
//   remoteVideoRef, 
//   callDuration, 
//   callType,
//   display,
//   callStatus,
//   callTypeRef,
//   acceptCall,
//   endCall,
//   receiverId,
//   senderId,
//   toggleScreenShare,
//   isScreenSharing,
// }) => {
//   const theme = useTheme();
//   const apiUrl = process.env.REACT_APP_API_URL;

//   // Enhanced animations
//   const pulseGlow = keyframes`
//     0%, 100% {
//       opacity: 1;
//       box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
//       transform: scale(1);
//     }
//     50% {
//       opacity: 0.95;
//       box-shadow: 0 8px 32px rgba(31, 38, 135, 0.25);
//       transform: scale(1.02);
//     }
//   `;

//   const fadeIn = keyframes`
//     from {
//       opacity: 0;
//       transform: scale(0.95);
//     }
//     to {
//       opacity: 1;
//       transform: scale(1);
//     }
//   `;

//   return (
//     <Box
//       sx={{
//         display: 'flex',
//         position: 'relative',
//         width: callType === 'audio' ? {
//           xs: '50%',
//           sm: '70%',
//           md: '30%'
//         } : '100%',
//         height: '100%',
//         justifyContent: 'center',
//         alignItems: 'center',
//         animation: `${fadeIn} 0.3s ease-out`,
//         padding: theme.spacing(2),
//         backgroundColor: 'rgba(0, 0, 0, 0.02)',
//         backdropFilter: 'blur(10px)',
//         borderRadius: theme.spacing(3),
//         overflow: 'hidden',
//       }}
//     >
//       {/* Main Container Background */}
//       <Box
//         sx={{
//           position: 'absolute',
//           inset: 0,
//           background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0))',
//           backdropFilter: 'blur(10px)',
//           border: '1px solid rgba(255,255,255,0.18)',
//           borderRadius: theme.spacing(3),
//           zIndex: 0,
//         }}
//       />

//       {/* Local Video */}
//       <Box
//         sx={{
//           display: callType === 'audio' ? 'none' : 'block',
//           position: 'absolute',
//           bottom: theme.spacing(3),
//           right: theme.spacing(3),
//           width: '25%',
//           aspectRatio: '16/9',
//           zIndex: 20,
//           borderRadius: theme.spacing(2),
//           overflow: 'hidden',
//           boxShadow: '0 8px 32px rgba(31, 38, 135, 0.2)',
//           transition: 'transform 0.3s ease',
//           '&:hover': {
//             transform: 'scale(1.05)',
//           },
//           '& video': {
//             width: '100%',
//             height: '100%',
//             objectFit: 'cover',
//             borderRadius: theme.spacing(2),
//           }
//         }}
//       >
//         <video ref={localVideoRef} autoPlay muted />
//       </Box>

//       {/* Call Duration Display */}
//       <Box
//         sx={{
//           position: 'absolute',
//           top: theme.spacing(3),
//           left: '50%',
//           transform: 'translateX(-50%)',
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center',
//           padding: theme.spacing(2, 4),
//           color: '#fff',
//           background: callType === 'audio'
//             ? 'linear-gradient(135deg, rgba(0,0,0,0.8), rgba(51,51,51,0.8))'
//             : 'linear-gradient(135deg, rgba(205,205,205,0.8), rgba(0,0,0,0.8))',
//           borderRadius: theme.spacing(3),
//           backdropFilter: 'blur(5px)',
//           boxShadow: '0 8px 32px rgba(31, 38, 135, 0.2)',
//           animation: `${pulseGlow} 3s infinite`,
//           zIndex: 30,
//           '&::before': callType === 'audio' ? {
//             content: '"🎙️"',
//             fontSize: '2rem',
//             marginRight: theme.spacing(2),
//             filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
//           } : {},
//         }}
//       >
//         <Typography
//           variant="h4"
//           sx={{
//             fontWeight: '600',
//             textShadow: '0 2px 4px rgba(0,0,0,0.3)',
//             fontSize: callType === 'audio' ? '2rem' : '2.5rem',
//             letterSpacing: '0.05em',
//             fontFamily: '"Inter", sans-serif'
//           }}
//         >
//           {callDuration}
//         </Typography>
//       </Box>

//       {/* Remote Video */}
//       <Box
//         sx={{
//           display: callType === 'audio' ? 'none' : 'block',
//           position: 'relative',
//           width: '100%',
//           height: '100%',
//           borderRadius: theme.spacing(2),
//           overflow: 'hidden',
//           boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
//           '& video': {
//             width: '100%',
//             height: '100%',
//             objectFit: 'cover',
//             transition: 'transform 0.3s ease',
//           }
//         }}
//       >
//         <video ref={remoteVideoRef} autoPlay />
//       </Box>

//       {/* Audio Call Background */}
//       {callType === 'audio' && (
//         <Box
//           sx={{
//             position: 'absolute',
//             inset: 0,
//             background: 'linear-gradient(135deg, #2A2A72 0%, #009FFD 100%)',
//             opacity: 0.1,
//             zIndex: -1,
//           }}
//         />
//       )}
//        {/* Video Call Container */}
//        {display && callStatus && (
//           <Box className="video-call-container">
//             {(callStatus === "idle" || callStatus === "ended") && (
//               <VideoCallPopup
//                 receiverId={receiverId}
//                 senderId={senderId}
//                 apiUrl={apiUrl}
//               />
//             )}
            
//             {callStatus === "ringing" && (
//               <Box sx={{ display: "flex", gap: 2 }}>
//                 <IconButton onClick={acceptCall}>
//                   <img
//                     style={{ width: "30px", height: "30px" }}
//                     src="https://res.cloudinary.com/dwckvxlca/image/upload/v1726679798/grp6xmgiq8kyjfzdkfmd.png"
//                     alt="Accept"
//                   />
//                 </IconButton>
//                 <IconButton onClick={endCall}>
//                   <img
//                     style={{ width: "30px", height: "30px" }}
//                     src="https://res.cloudinary.com/dwckvxlca/image/upload/v1726679832/kk4vhf2u9axne003xixs.png"
//                     alt="End"
//                   />
//                 </IconButton>
//               </Box>
//             )}

//             {callStatus === "active" && (
//               <Box className="call-controls" sx={{ display: "flex", gap: 2 }}>
//                 <IconButton onClick={endCall}>
//                   <img
//                     style={{ width: "30px", height: "30px" }}
//                     src="https://res.cloudinary.com/dwckvxlca/image/upload/v1726679832/kk4vhf2u9axne003xixs.png"
//                     alt="End"
//                   />
//                 </IconButton>
//                 {callTypeRef?.current === "video" && (
//                   <IconButton onClick={toggleScreenShare}>
//                     <img
//                       style={{ width: "30px", height: "30px" }}
//                       src={
//                         isScreenSharing
//                           ? "https://res.cloudinary.com/dwckvxlca/image/upload/v1726679906/nl2bfcmhue28znzrtlta.png"
//                           : "https://res.cloudinary.com/dwckvxlca/image/upload/v1726679942/zvxhmbpojgqrpwb8w8xq.png"
//                       }
//                       alt="Screen Share"
//                     />
//                   </IconButton>
//                 )}
//               </Box>
//             )}
//           </Box>
//         )}
//     </Box>
//   );
// };

// export default VideoCallDisplay;